// src/Commoncontext/ServicerequestsContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import ServicerequestsApi from "../Api/ServicerequestsApi";
import { useSelectedCompany } from "./SelectedCompanyContext";

const ServicerequestsContext = createContext();

/**
 * Listan välimuisti per companyId ETag:lla (sama malli kuin Usersissa).
 */
const CACHE_KEY = "sr_cache_etag_v1";

function createCache() {
  return {
    map: new Map(), // companyId -> { data, version, etag, fetchedAt }
    hydrate() {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) this.map = new Map(Object.entries(JSON.parse(raw)));
      } catch {}
    },
    persist() {
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify(Object.fromEntries(this.map))
        );
      } catch {}
    },
  };
}

// ✅ pieni apuri: normalisoi version-kenttä samannimiseksi (future-proof)
function normalizeRequestVersion(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const v =
    obj.version ??
    obj.requestVersion ??
    obj._version ??
    0;

  // jos obj.version jo ok, palauta sellaisenaan
  if (obj.version === v) return obj;

  return { ...obj, version: v };
}

export const ServicerequestsProvider = ({ children }) => {
  const cache = useRef(createCache()).current;
  const { selectedCompanyId } = useSelectedCompany();

  const [requests, setRequests] = useState([]); // listanäkymä
  const [loading, setLoading] = useState(true);

  const [active, setActive] = useState(null); // detailinäkymä
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    cache.hydrate();
  }, [cache]);

  useEffect(() => {
    if (!active?.id || !selectedCompanyId) return;

    // Kuunnellaan vain aktiivisen requestin viestit
    let unsub = null;

    unsub = ServicerequestsApi.listenMessagesForRequest(
      selectedCompanyId,
      active.id,
      { pageSize: 200 },
      (items, err) => {
        if (err) {
          console.error("listenMessagesForRequest error:", err);
          return;
        }

        setActive((prev) => {
          if (!prev || prev.id !== active.id) return prev;

          const mergedMessages = mergeSnapshotWithOptimistic(
            items,
            prev.messages || []
          );

          const last = mergedMessages.length
            ? mergedMessages[mergedMessages.length - 1]
            : null;

          return {
            ...prev,
            messages: mergedMessages,
            lastMessageAt: last?.createdAt ?? prev.lastMessageAt,
            updatedAt: last?.createdAt ?? prev.updatedAt,
          };
        });
      }
    );

    return () => {
      try {
        unsub && unsub();
      } catch {}
    };
  }, [active?.id, selectedCompanyId]);

  /* ============================ LISTA ============================ */
  const fetchRequests = useCallback(
    async (companyId) => {
      if (!companyId) return;

      const entry = cache.map.get(companyId);
      if (entry) setRequests(entry.data);

      setLoading(!entry);

      try {
        const resp = await ServicerequestsApi.fetchRequests(companyId, {
          etag: entry?.etag,
        });

        if (resp.status === 304) {
          return;
        }

        // ✅ jos backend alkaa myöhemmin palauttaa request.version listassa, normalisoidaan se
        const normalizedList = (resp.requests || []).map(normalizeRequestVersion);

        const newEntry = {
          data: normalizedList,
          version: resp.version,
          etag: resp.etag,
          fetchedAt: Date.now(),
        };

        cache.map.set(companyId, newEntry);
        cache.persist();
        setRequests(normalizedList);
      } catch (e) {
        console.error("fetchRequests error:", e);
        if (!entry) setRequests([]);
      } finally {
        setLoading(false);
      }
    },
    [cache]
  );

  /* ============================ DETALJI ============================ */
  const fetchRequestById = useCallback(async (companyId, requestId) => {
    if (!companyId || !requestId) return;

    setDetailLoading(true);
    try {
      const r = await ServicerequestsApi.fetchRequestById(companyId, requestId);

      // ✅ varmista että detailissä on aina .version (optimistic concurrency)
      const normalized = normalizeRequestVersion(r);

      setActive(normalized);
      return normalized;
    } catch (e) {
      console.error("fetchRequestById error:", e);
      setActive(null);
      return null;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  /* ============================ LUO ============================ */
  const createRequest = useCallback(
    async (
      companyId,
      {
        type,
        title,
        apartmentId,
        priority,
        description,
        createdBy,
        assigneeName,
      }
    ) => {
      const res = await ServicerequestsApi.createRequest({
        companyId,
        type,
        title,
        apartmentId,
        priority,
        description,
        createdBy,
        assigneeName,
      });

      // Optimistinen lisäys listaan
      setRequests((prev) => {
        const optimistic = normalizeRequestVersion({
          id: res.id,
          number: res.number,
          type,
          title,
          apartmentId: apartmentId || "",
          status: "uusi",
          priority: priority || "normal",
          assigneeName: assigneeName || null,
          description: description || "",
          createdBy: createdBy || null,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          lastMessageAt: null,
          version: 0, // ✅ uusi luotu alkaa yleensä 0 tai 1; backendin päätös
        });
        return [optimistic, ...(prev || [])];
      });

      // Invalidate ETag
      const entry = cache.map.get(companyId);
      if (entry) {
        cache.map.set(companyId, { ...entry, etag: undefined });
        cache.persist();
      }

      return res;
    },
    [cache]
  );

  /* ============================ PÄIVITYKSET ============================ */
  const updateRequest = useCallback(
    async (companyId, requestId, patch) => {
      // ✅ expectedVersion aktiivisesta requestista (optimistic concurrency)
      const expectedVersion =
        active && active.id === requestId
          ? (active.version ?? active.requestVersion ?? 0)
          : undefined;

      try {
        // ✅ pass expectedVersion API:lle
        const resp = await ServicerequestsApi.updateRequest(
          companyId,
          requestId,
          patch,
          { expectedVersion }
        );

        // ✅ backend palauttaa (suositus): { ok:true, version:<newVersion> }
        const newVersion =
          resp?.version ??
          resp?.requestVersion ??
          (expectedVersion !== undefined ? expectedVersion + 1 : undefined);

        setRequests((prev) =>
          (prev || []).map((r) =>
            r.id === requestId
              ? normalizeRequestVersion({
                  ...r,
                  ...patch,
                  updatedAt: Date.now(),
                  // jos listassa on version käytössä, päivitetään
                  ...(newVersion !== undefined ? { version: newVersion } : {}),
                })
              : r
          )
        );

        setActive((prev) =>
          prev && prev.id === requestId
            ? normalizeRequestVersion({
                ...prev,
                ...patch,
                updatedAt: Date.now(),
                ...(newVersion !== undefined ? { version: newVersion } : {}),
              })
            : prev
        );

        const entry = cache.map.get(companyId);
        if (entry) {
          cache.map.set(companyId, { ...entry, etag: undefined });
          cache.persist();
        }

        return resp;
      } catch (e) {
        // ✅ TÄRKEÄ: conflict pitää päästä ylös Servicerequests.js:ään popupia varten
        // Api.buildError asettaa: e.code="conflict" ja e.server=...
        if (String(e?.code || "") === "conflict") {
          throw e;
        }
        throw e;
      }
    },
    [cache, active]
  );

  const addMessage = useCallback(
    async (companyId, requestId, payload = {}) => {
      const { body, visibility = "public", senderName = "" } = payload || {};

      const trimmed = typeof body === "string" ? body.trim() : "";
      if (!companyId || !requestId || !trimmed) return null;

      const clientMessageId = `m_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2)}`;

      const optimistic = {
        id: clientMessageId,
        body: trimmed,
        visibility: visibility === "internal" ? "internal" : "public",
        senderId: null,
        senderName,
        createdAt: Date.now(),
        _optimistic: true,
      };

      setActive((prev) => {
        if (!prev || prev.id !== requestId) return prev;

        const prevMsgs = Array.isArray(prev.messages) ? prev.messages : [];
        if (prevMsgs.some((m) => String(m?.id) === String(clientMessageId)))
          return prev;

        const now = Date.now();
        return {
          ...prev,
          messages: [...prevMsgs, optimistic],
          lastMessageAt: now,
          updatedAt: now,
        };
      });

      setRequests((prev) =>
        (prev || []).map((r) =>
          r.id === requestId
            ? { ...r, lastMessageAt: Date.now(), updatedAt: Date.now() }
            : r
        )
      );

      try {
        const saved = await ServicerequestsApi.addMessage(companyId, requestId, {
          body: trimmed,
          visibility,
          senderName,
          clientMessageId,
        });

        setActive((prev) => {
          if (!prev || prev.id !== requestId) return prev;

          const prevMsgs = Array.isArray(prev.messages) ? prev.messages : [];
          const nextMsgs = prevMsgs.map((m) =>
            String(m?.id) === String(clientMessageId)
              ? { ...saved, _optimistic: false }
              : m
          );

          return {
            ...prev,
            messages: nextMsgs,
            lastMessageAt: saved.createdAt,
            updatedAt: saved.createdAt,
          };
        });

        const entry = cache.map.get(companyId);
        if (entry) {
          cache.map.set(companyId, { ...entry, etag: undefined });
          cache.persist();
        }

        return saved;
      } catch (e) {
        setActive((prev) => {
          if (!prev || prev.id !== requestId) return prev;
          return {
            ...prev,
            messages: (prev.messages || []).filter(
              (m) => String(m?.id) !== String(clientMessageId)
            ),
          };
        });
        throw e;
      }
    },
    [cache]
  );

  const deleteMessage = useCallback(async (companyId, requestId, messageId) => {
    if (!companyId || !requestId || !messageId) return null;

    setActive((prev) => {
      if (!prev || prev.id !== requestId) return prev;
      const nextMsgs = (prev.messages || []).map((m) =>
        String(m.id) === String(messageId)
          ? { ...m, isDeleted: true, body: "" }
          : m
      );
      return { ...prev, messages: nextMsgs };
    });

    try {
      const res = await ServicerequestsApi.deleteMessage(
        companyId,
        requestId,
        messageId
      );
      return res;
    } catch (e) {
      setActive((prev) => {
        if (!prev || prev.id !== requestId) return prev;
        const nextMsgs = (prev.messages || []).map((m) => {
          if (String(m.id) !== String(messageId)) return m;
          const restoredBody =
            typeof m._backupBody === "string" ? m._backupBody : m.body;
          return { ...m, isDeleted: false, body: restoredBody };
        });
        return { ...prev, messages: nextMsgs };
      });

      throw e;
    }
  }, []);

  /* ============================ LIITTEET ============================ */
  const uploadAttachment = useCallback(
    async (companyId, requestId, file) => {
      const att = await ServicerequestsApi.uploadAttachment(
        companyId,
        requestId,
        file
      );

      setActive((prev) => {
        if (!prev || prev.id !== requestId) return prev;
        return {
          ...prev,
          attachments: [...(prev.attachments || []), att],
          updatedAt: Date.now(),
        };
      });

      const entry = cache.map.get(companyId);
      if (entry) {
        cache.map.set(companyId, { ...entry, etag: undefined });
        cache.persist();
      }

      return att;
    },
    [cache]
  );

  const uploadAttachments = useCallback(
    async (companyId, requestId, files) => {
      const arr = Array.from(files || []);
      const results = [];
      for (const f of arr) {
        const one = await uploadAttachment(companyId, requestId, f);
        results.push(one);
      }
      return results;
    },
    [uploadAttachment]
  );

  const deleteAttachment = useCallback(
    async (companyId, requestId, attachmentId) => {
      await ServicerequestsApi.deleteAttachment(
        companyId,
        requestId,
        attachmentId
      );

      setActive((prev) => {
        if (!prev || prev.id !== requestId) return prev;
        return {
          ...prev,
          attachments: (prev.attachments || []).filter(
            (a) => a.id !== attachmentId
          ),
          updatedAt: Date.now(),
        };
      });

      const entry = cache.map.get(companyId);
      if (entry) {
        cache.map.set(companyId, { ...entry, etag: undefined });
        cache.persist();
      }
    },
    [cache]
  );

  /* ============================ POISTA PYYNTÖ ============================ */
  const deleteRequest = useCallback(async (companyId, requestId) => {
    if (!companyId || !requestId) return;

    await ServicerequestsApi.deleteRequest(companyId, requestId);
    // Poista listasta
    setRequests((prev) => (prev || []).filter((r) => r.id !== requestId));
    // Jos aktiivinen oli tämä, sulje se
    setActive((prev) => (prev && prev.id === requestId ? null : prev));
    // Invalidate ETag
    const entry = cache.map.get(companyId);
    if (entry) {
      cache.map.set(companyId, { ...entry, etag: undefined });
      cache.persist();
    }
  }, [cache]);

  /** Yhdistä snapshot-viestit + mahdolliset optimistiset (tmp_*) viestit ilman duplikaatteja. */
  function mergeSnapshotWithOptimistic(snapshotMessages, currentMessages) {
    const snapArr = Array.isArray(snapshotMessages) ? snapshotMessages : [];
    const curArr = Array.isArray(currentMessages) ? currentMessages : [];

    const snapIds = new Set(snapArr.map((m) => String(m?.id)));

    const optimisticKept = curArr.filter((m) => {
      const id = String(m?.id || "");
      const isOptimistic = id.startsWith("tmp_") || m?._optimistic;
      return isOptimistic && !snapIds.has(id);
    });

    const merged = [...snapArr, ...optimisticKept];
    merged.sort((a, b) => (a?.createdAt || 0) - (b?.createdAt || 0));
    return merged;
  }

  return (
    <ServicerequestsContext.Provider
      value={{
        // lista
        requests,
        loading,
        fetchRequests,

        // detalji
        active,
        detailLoading,
        fetchRequestById,
        setActive,

        // toiminnot
        createRequest,
        updateRequest, // ✅ nyt tukee expectedVersion + heittää conflictin ulos
        addMessage,
        deleteMessage,
        uploadAttachment,
        uploadAttachments,
        deleteAttachment,
        deleteRequest,
      }}
    >
      {children}
    </ServicerequestsContext.Provider>
  );
};

export const useServicerequests = () => useContext(ServicerequestsContext);